declare global {
  interface Window {
    __ENV__?: {
      VITE_API_URL?: string;
      VITE_ROCKETCHAT_URL?: string;
      VITE_ENABLE_CHAT?: string;
      VITE_ENABLE_CHAT_ADMIN?: string;
      VITE_ENABLE_BANKING?: string;
      VITE_IS_DEMO?: string;
      VITE_USE_MOCK_API?: string;
      VITE_1C_ACCOUNTING?: string;
    };
  }
}

function readEnv(key: keyof NonNullable<Window["__ENV__"]>): string {
  return window.__ENV__?.[key] ?? "";
}

function readFlag(key: keyof NonNullable<Window["__ENV__"]>): boolean {
  return readEnv(key) === "true";
}

const DEMO_API_HOSTS = ["demo.", "localhost", "127.0.0.1"];

const ONE_C_DISABLED_VALUES = ["", "false", "0", "off", "no"];

export type OneCAccountingConfig =
  | { enabled: false }
  | { enabled: true; source: "mock" }
  | { enabled: true; source: "remote"; baseUrl: string };

const ONE_C_MOCK_VALUE = "mock";

function readOneCAccounting(): OneCAccountingConfig {
  const raw = readEnv("VITE_1C_ACCOUNTING").trim();
  if (ONE_C_DISABLED_VALUES.includes(raw.toLowerCase())) return { enabled: false };
  if (raw.toLowerCase() === ONE_C_MOCK_VALUE) return { enabled: true, source: "mock" };

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    throw new Error(
      `Refusing to start: VITE_1C_ACCOUNTING must be a disabling value ` +
        `(${ONE_C_DISABLED_VALUES.filter(Boolean).join(", ")}), the literal "${ONE_C_MOCK_VALUE}", ` +
        `or an absolute http(s) URL, got "${raw}".`,
    );
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new Error(
      `Refusing to start: VITE_1C_ACCOUNTING must use http or https, got "${parsed.protocol}" in "${raw}".`,
    );
  }

  return { enabled: true, source: "remote", baseUrl: raw.replace(/\/+$/, "") };
}

function assertDemoModeIsNotPointedAtProduction(apiUrl: string, isDemo: boolean): void {
  if (!isDemo) return;
  const apiOrigin = apiUrl || window.location.origin;
  if (DEMO_API_HOSTS.some((host) => apiOrigin.includes(host))) return;
  throw new Error(
    `Refusing to start: VITE_IS_DEMO=true with API origin ${apiOrigin}. ` +
      "Demo mode bypasses NCALayer and submits an empty signature instead of a real one.",
  );
}

const apiUrl = readEnv("VITE_API_URL").replace(/\/+$/, "");
const isDemo = readFlag("VITE_IS_DEMO");
const useMockApi = readFlag("VITE_USE_MOCK_API");
const oneCAccounting = readOneCAccounting();

assertDemoModeIsNotPointedAtProduction(apiUrl, isDemo);

export const runtimeConfig = {
  apiUrl,
  rocketchatUrl: readEnv("VITE_ROCKETCHAT_URL"),
  enableChat: readFlag("VITE_ENABLE_CHAT"),
  enableChatAdmin: readFlag("VITE_ENABLE_CHAT_ADMIN"),
  enableBanking: readFlag("VITE_ENABLE_BANKING"),
  isDemo,
  useMockApi,
  oneCAccounting,
};

export const isInternalAccountingEnabled = !oneCAccounting.enabled;

export const isAccountingReadOnly = oneCAccounting.enabled;

export {};
